"use client";

import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { MapContext } from "./context";
import type { ViewState } from "./types";

interface MapProviderProps {
  /**
   * Mapbox access token. Falls back to the NEXT_PUBLIC_MAPBOX_TOKEN
   * environment variable if not provided.
   */
  accessToken?: string;
  children?: React.ReactNode;
  initialViewState: ViewState;
  mapContainerRef: React.RefObject<HTMLDivElement | null>;
}

export function MapProvider({
  mapContainerRef,
  initialViewState,
  accessToken,
  children,
}: MapProviderProps) {
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [mapInstance, setMapInstance] = useState<mapboxgl.Map | null>(null);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) {
      return;
    }

    const token = accessToken ?? process.env.NEXT_PUBLIC_MAPBOX_TOKEN ?? "";
    mapboxgl.accessToken = token;

    const m = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/standard",
      center: [initialViewState.longitude, initialViewState.latitude],
      zoom: initialViewState.zoom,
      attributionControl: false,
      logoPosition: "bottom-right",
    });

    mapRef.current = m;

    m.on("load", () => {
      setMapInstance(m);
    });

    return () => {
      m.remove();
      mapRef.current = null;
      setMapInstance(null);
    };
  }, [initialViewState, mapContainerRef, accessToken]);

  return (
    <div className="z-[1000]">
      <MapContext.Provider value={{ map: mapInstance }}>
        {children}
      </MapContext.Provider>
      {!mapInstance && (
        <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-background/80">
          <div className="font-medium text-lg">Loading map...</div>
        </div>
      )}
    </div>
  );
}
